import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

/**
 * Reusable data-driven table for blog articles and guide pages.
 *
 * @param {Object[]} columns - Column definitions: { key, label, align?, headerSx? }
 * @param {Object[]} rows    - Row data. Each cell value is a string OR { text, sx } for custom styling.
 * @param {Object[]} [footer] - Optional bold footer/total rows (same shape as rows).
 * @param {string}   [caption] - Accessible <caption> text.
 * @param {string}   [headerBg='#1E3A5F'] - Header background color.
 */
const ArticleTable = ({ columns, rows, footer, caption, headerBg = '#1E3A5F' }) => {
  const isLightHeader = headerBg !== '#1E3A5F';
  const defaultHeaderColor = isLightHeader ? '#1A1A1A' : '#fff';

  const renderCell = (row, col, idx) => {
    const value = row[col.key];
    const isFirst = idx === 0;

    if (value && typeof value === 'object' && value.text !== undefined) {
      return (
        <TableCell
          key={col.key}
          align={col.align}
          sx={{ fontWeight: isFirst ? 600 : 400, color: '#1A1A1A', ...value.sx }}
        >
          {value.text}
        </TableCell>
      );
    }

    return (
      <TableCell
        key={col.key}
        align={col.align}
        sx={{ fontWeight: isFirst ? 600 : 400, color: '#1A1A1A' }}
      >
        {value}
      </TableCell>
    );
  };

  return (
    <TableContainer
      component={Paper}
      className="mobile-scroll-container"
      sx={{
        borderRadius: '10px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
        mb: 3,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Table>
        {caption && <caption style={{ captionSide: 'bottom', padding: '8px', color: '#5F6B7A', fontSize: '0.8rem' }}>{caption}</caption>}
        <TableHead>
          <TableRow sx={{ bgcolor: headerBg }}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align}
                sx={{
                  backgroundColor: headerBg,
                  color: defaultHeaderColor,
                  fontWeight: 600,
                  ...col.headerSx,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIdx) => (
            <TableRow
              key={rowIdx}
              sx={{
                '&:nth-of-type(even)': { bgcolor: '#F7F9FC' },
                '&:hover': { bgcolor: '#F0F2F5' },
                transition: 'background-color 0.15s ease',
              }}
            >
              {columns.map((col, colIdx) => renderCell(row, col, colIdx))}
            </TableRow>
          ))}
          {footer &&
            footer.map((fRow, fIdx) => (
              <TableRow key={`footer-${fIdx}`} sx={{ bgcolor: '#F7F9FC' }}>
                {columns.map((col) => {
                  const value = fRow[col.key];
                  const text = value && typeof value === 'object' ? value.text : value;
                  const extraSx = value && typeof value === 'object' ? value.sx : {};
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        color: '#1E3A5F',
                        fontSize: '1rem',
                        ...extraSx,
                      }}
                    >
                      {text}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ArticleTable;
